/**
 * Saved views and the field registry. Destination: `@renote/contracts`.
 *
 * The registry is the single source of truth consumed by two callers: the SQL
 * compiler uses it as the allowlist for filter/sort/column fields, and the web
 * app builds its filter and column menus from the same payload. A field that is
 * not in the registry is neither filterable nor displayable — there is no second
 * list to keep in sync.
 */

import { z } from 'zod'
import {
  fieldDescriptor,
  instant,
  uuid,
  viewColumn,
  viewFilter,
  viewSort,
} from './common'

export const viewEntity = z.enum(['lead', 'deal', 'organization', 'contact', 'task'])
export type ViewEntity = z.infer<typeof viewEntity>

export const viewType = z.enum(['list', 'kanban', 'group_by'])
export type ViewType = z.infer<typeof viewType>

/**
 * Kanban board configuration. `columnOrder` pins the left-to-right order when
 * the group-by field's own ordering is not what the team wants; `collapsed`
 * remembers folded columns per view, not per user, which is the behaviour
 * people expect from a shared board.
 */
export const kanbanSettings = z
  .object({
    columnOrder: z.array(z.string()).default([]),
    collapsed: z.array(z.string()).default([]),
    showEmptyColumns: z.boolean().default(true),
    cardFields: z.array(z.string()).max(6).default([]),
  })
  .strict()
export type KanbanSettings = z.infer<typeof kanbanSettings>

export const savedView = z.object({
  id: uuid,
  name: z.string(),
  entity: viewEntity,
  viewType: viewType,
  filters: z.array(viewFilter),
  sort: z.array(viewSort),
  columns: z.array(viewColumn),
  groupByField: z.string().nullable(),
  kanbanSettings: kanbanSettings,
  ownerId: uuid,
  isShared: z.boolean(),
  isPinned: z.boolean(),
  position: z.number().int(),
  createdAt: instant,
  updatedAt: instant,
})
export type SavedView = z.infer<typeof savedView>

export const upsertSavedViewInput = z
  .object({
    name: z.string().trim().min(1).max(120),
    entity: viewEntity,
    viewType: viewType.default('list'),
    filters: z.array(viewFilter).max(25).default([]),
    sort: z.array(viewSort).max(5).default([]),
    columns: z.array(viewColumn).max(40).default([]),
    groupByField: z.string().max(64).nullable(),
    kanbanSettings: kanbanSettings.default({
      columnOrder: [],
      collapsed: [],
      showEmptyColumns: true,
      cardFields: [],
    }),
    isShared: z.boolean().default(false),
    isPinned: z.boolean().default(false),
    position: z.number().int().nonnegative().default(0),
  })
  .strict()
  // mirrors crm_saved_view_kanban_needs_group_by
  .refine((v) => v.viewType !== 'kanban' || v.groupByField !== null, {
    message: 'A kanban view needs a field to group by',
    path: ['groupByField'],
  })
export type UpsertSavedViewInput = z.infer<typeof upsertSavedViewInput>

/**
 * Ad-hoc view state, sent by the list screen when the user has filtered without
 * saving. Same shape as a saved view's body, so the compiler takes one input
 * type whether the state came from a saved row or from the URL.
 */
export const viewState = z
  .object({
    filters: z.array(viewFilter).max(25).default([]),
    sort: z.array(viewSort).max(5).default([]),
    columns: z.array(viewColumn).max(40).default([]),
    groupByField: z.string().max(64).nullable().default(null),
  })
  .strict()
export type ViewState = z.infer<typeof viewState>

/** GET /crm/registry/:entity — drives both the compiler and the filter menu. */
export const fieldRegistry = z.object({
  entity: viewEntity,
  fields: z.array(fieldDescriptor),
  defaultColumns: z.array(z.string()),
  defaultSort: z.array(viewSort),
  /** Fields that can be a kanban axis — bounded option sets only. */
  groupableFields: z.array(z.string()),
})
export type FieldRegistry = z.infer<typeof fieldRegistry>
